import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  Clock,
  IndianRupee,
  MapPin,
  X,
  Users,
  Calendar,
  CheckCircle,
  Star,
  Shield,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

function BangaloreSection() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    adults: 1,
    children: 0,
    pickup: "",
  });

  const [errors, setErrors] = useState({});

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const userData = localStorage.getItem("userData");
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setIsAuthenticated(true);

        setFormData(prev => ({
          ...prev,
          name: parsedUser.name || "",
          email: parsedUser.email || "",
          phone: parsedUser.phone || "",
        }));
      } catch (err) {
        console.error("Invalid user data in localStorage");
        localStorage.removeItem("authToken");
        localStorage.removeItem("userData");
      }
    }
  }, []);

  const handleBookNowClick = () => {
    navigate('/trips');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "adults" || name === "children" ? Number(value) || 0 : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone is required";
    } else if (!/^[0-9]{10}$/.test(formData.phone.replace(/[\s-]/g, ''))) {
      newErrors.phone = "Please enter a valid 10-digit phone number";
    }

    // Date validation
    if (!formData.date) {
      newErrors.date = "Please select a tour date";
    } else {
      const selectedDate = new Date(formData.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        newErrors.date = "Date cannot be in the past";
      }
    }

    // Pickup validation
    if (!formData.pickup.trim()) {
      newErrors.pickup = "Pickup location is required";
    }

    // Adults validation
    if (formData.adults < 1) {
      newErrors.adults = "At least 1 adult is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Here you would normally send data to backend
      console.log("Booking submitted:", formData);

      // Success state
      alert(" Booking request submitted successfully! Our team will contact you shortly.");
      setShowBookingForm(false);

      // Reset form
      setFormData({
        name: user?.name || "",
        email: user?.email || "",
        phone: user?.phone || "",
        date: "",
        adults: 1,
        children: 0,
        pickup: "",
      });
      setErrors({});
    } catch (error) {
      console.error("Booking error:", error);
      alert("Sorry, something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalPrice = formData.adults * 2099 + formData.children * 1500;

  const highlights = [
    { icon: Star, text: "4.8★ Rated Experience" },
    // { icon: Users, text: "Small Group Tours" },
    { icon: Shield, text: "100% Safe & Secure" },
    { icon: CheckCircle, text: "Instant Confirmation" },
  ];

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-purple-100 via-indigo-50 to-white">
      {/* Animated Background Elements */}
      <div className="absolute -top-20 -left-20 w-72 h-72 bg-purple-300/30 rounded-full blur-3xl animate-pulse" />
      <div className="absolute top-40 -right-20 w-96 h-96 bg-indigo-300/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      <div className="absolute bottom-20 left-1/2 w-64 h-64 bg-pink-300/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20 text-center">
        {/* New Badge */}
        <div
          className="inline-flex items-center gap-2 bg-white/80 border border-purple-200 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium mb-6 sm:mb-8 shadow-sm backdrop-blur-sm hover:bg-white/90 hover:shadow-md transition-all duration-300 cursor-pointer"
          role="status"
          aria-label="New tour announcement"
        >
          <span className="flex items-center gap-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap">
            {/* <Sparkles className="w-3 h-3" /> */}
            New
          </span>
          <span className="text-gray-700 truncate sm:whitespace-normal">
            Explore Bangalore in One Perfect Day
          </span>
        </div>

        {/* Main Heading */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-4 sm:mb-6 leading-tight">
          <span className="bg-gradient-to-r from-purple-600 via-indigo-600 to-pink-600 bg-clip-text text-transparent">
            Bangalore City Tour
          </span>
        </h1>

        {/* Description */}
        <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto mb-6 sm:mb-8 leading-relaxed">
          Experience Bangalore's lush gardens, rich heritage, and vibrant city life in one unforgettable day.
        </p>

        {/* Key Information */}
        <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 lg:gap-8 text-sm sm:text-base lg:text-lg mb-8 sm:mb-10">
          <div className="flex items-center gap-2 bg-white/60 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full shadow-sm hover:shadow-md transition-shadow">
            <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
            <span className="font-medium">10-12 Hours</span>
          </div>
          <div className="flex items-center gap-2 bg-white/60 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full shadow-sm hover:shadow-md transition-shadow">
            <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
            <span className="font-medium">7 Attractions</span>
          </div>

        </div>

        <div className="grid grid-cols-3 lg:grid-cols-3 gap-2.5 sm:gap-4 lg:gap-6 max-w-full mx-auto mb-6 sm:mb-10 px-2 sm:px-4">
          {highlights.map((item, index) => {
            const Icon = item.icon;
            return (
              <div
                key={index}
                className="flex flex-col items-center justify-center gap-1.5 sm:gap-3 
                   bg-white/70 backdrop-blur-sm 
                   p-2.5 sm:p-6 md:p-8
                   rounded-md sm:rounded-xl 
                   shadow-sm hover:shadow-md 
                   transition-all duration-300 
                   hover:-translate-y-1"
              >
                <Icon className="w-4 h-4 sm:w-7 sm:h-7 text-purple-600" />
                <span className="text-[10px] sm:text-sm md:text-base font-medium text-gray-700 text-center leading-tight">
                  {item.text}
                </span>
              </div>
            );
          })}
        </div>

        {/* CTA Button */}
        <button
          onClick={handleBookNowClick}
          className="group relative inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 via-orange-600 to-red-600 text-white px-8 sm:px-12 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg lg:text-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 shadow-lg overflow-hidden"
          aria-label="Book Bangalore city tour now"
        >
          <span className="absolute inset-0 bg-gradient-to-r from-orange-600 to-red-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <span className="relative flex items-center gap-2">
            Book Now
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </span>
        </button>

        {/* Trust Indicators */}
        <div className="mt-6 sm:mt-8 flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span>Instant Booking</span>
          </div>
          <div className="flex items-center gap-1">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span>Best Price Guarantee</span>
          </div>
          <div className="flex items-center gap-1">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span>Free Cancellation</span>
          </div>
        </div>

        {/* Login Prompt */}
        {showLoginPrompt && (
          <div className="mt-6 sm:mt-8 inline-flex items-center gap-3 bg-white/90 border border-orange-200 px-4 sm:px-6 py-3 sm:py-4 rounded-2xl shadow-md animate-fade-in">
            <div className="text-left">
              <p className="font-medium text-gray-800 text-sm sm:text-base">
                Please login first to book this tour
              </p>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">
                You'll be able to continue right after signing in
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Booking Modal */}
      {isAuthenticated && showBookingForm && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in"
          onClick={() => setShowBookingForm(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="booking-modal-title"
        >
          <div
            className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white p-4 sm:p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 id="booking-modal-title" className="text-xl sm:text-2xl font-bold mb-1">
                    Book Your Bangalore Tour
                  </h3>
                  <p className="text-orange-100 text-sm">
                    Complete the details below to confirm your booking
                  </p>
                </div>
                <button
                  onClick={() => setShowBookingForm(false)}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-colors flex-shrink-0"
                  aria-label="Close booking form"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
              <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                {/* Personal Information */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-orange-600" />
                    Personal Information
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter your full name"
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${errors.name
                          ? "border-red-500 focus:ring-red-200"
                          : "border-gray-300 focus:ring-orange-200 focus:border-orange-500"
                          }`}
                        required
                        aria-invalid={errors.name ? "true" : "false"}
                        aria-describedby={errors.name ? "name-error" : undefined}
                      />
                      {errors.name && (
                        <p id="name-error" className="text-red-500 text-sm mt-1 flex items-center gap-1">
                          <span className="text-xs">⚠</span> {errors.name}
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="your.email@example.com"
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${errors.email
                          ? "border-red-500 focus:ring-red-200"
                          : "border-gray-300 focus:ring-orange-200 focus:border-orange-500"
                          }`}
                        required
                        aria-invalid={errors.email ? "true" : "false"}
                        aria-describedby={errors.email ? "email-error" : undefined}
                      />
                      {errors.email && (
                        <p id="email-error" className="text-red-500 text-sm mt-1 flex items-center gap-1">
                          <span className="text-xs">⚠</span> {errors.email}
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="10-digit mobile number"
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${errors.phone
                          ? "border-red-500 focus:ring-red-200"
                          : "border-gray-300 focus:ring-orange-200 focus:border-orange-500"
                          }`}
                        required
                        aria-invalid={errors.phone ? "true" : "false"}
                        aria-describedby={errors.phone ? "phone-error" : undefined}
                      />
                      {errors.phone && (
                        <p id="phone-error" className="text-red-500 text-sm mt-1 flex items-center gap-1">
                          <span className="text-xs">⚠</span> {errors.phone}
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
                        Tour Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="date"
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        min={getMinDate()}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${errors.date
                          ? "border-red-500 focus:ring-red-200"
                          : "border-gray-300 focus:ring-orange-200 focus:border-orange-500"
                          }`}
                        required
                        aria-invalid={errors.date ? "true" : "false"}
                        aria-describedby={errors.date ? "date-error" : undefined}
                      />
                      {errors.date && (
                        <p id="date-error" className="text-red-500 text-sm mt-1 flex items-center gap-1">
                          <span className="text-xs">⚠</span> {errors.date}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Group Size */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-orange-600" />
                    Group Size
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="adults" className="block text-sm font-medium text-gray-700 mb-2">
                        Adults <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          id="adults"
                          type="number"
                          name="adults"
                          value={formData.adults}
                          onChange={handleInputChange}
                          min="1"
                          max="20"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-500 transition-all"
                          required
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                          ₹2,199 each
                        </div>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="children" className="block text-sm font-medium text-gray-700 mb-2">
                        Children
                      </label>
                      <div className="relative">
                        <input
                          id="children"
                          type="number"
                          name="children"
                          value={formData.children}
                          onChange={handleInputChange}
                          min="0"
                          max="20"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-500 transition-all"
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                          ₹1,500 each
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Children under 12 years get special pricing
                  </p>
                </div>

                {/* Pickup Location */}
                <div>
                  <label htmlFor="pickup" className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-orange-600" />
                      Pickup Location <span className="text-red-500">*</span>
                    </span>
                  </label>
                  <input
                    id="pickup"
                    type="text"
                    name="pickup"
                    value={formData.pickup}
                    onChange={handleInputChange}
                    placeholder="e.g., Airport, Hotel name, or specific area"
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${errors.pickup
                      ? "border-red-500 focus:ring-red-200"
                      : "border-gray-300 focus:ring-orange-200 focus:border-orange-500"
                      }`}
                    required
                    aria-invalid={errors.pickup ? "true" : "false"}
                    aria-describedby={errors.pickup ? "pickup-error" : undefined}
                  />
                  {errors.pickup && (
                    <p id="pickup-error" className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <span className="text-xs">⚠</span> {errors.pickup}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Default pickups: Bellandur (7:00 AM) or Marathahalli (7:30 AM)
                  </p>
                </div>

                {/* Price Summary */}
                <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-4 sm:p-6 border border-orange-100">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Booking Summary
                  </h4>
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        {formData.adults} Adult{formData.adults !== 1 ? 's' : ''} × ₹2,199
                      </span>
                      <span className="font-semibold">
                        ₹{(formData.adults * 2099).toLocaleString("en-IN")}
                      </span>
                    </div>
                    {formData.children > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          {formData.children} Child{formData.children !== 1 ? 'ren' : ''} × ₹1,500
                        </span>
                        <span className="font-semibold">
                          ₹{(formData.children * 1500).toLocaleString("en-IN")}
                        </span>
                      </div>
                    )}
                    <div className="border-t border-orange-200 pt-2 mt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700 font-semibold">Total Amount:</span>
                        <span className="text-2xl sm:text-3xl font-bold text-orange-700">
                          ₹{totalPrice.toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-xs text-gray-600 bg-white/50 p-2 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Includes all entry tickets, breakfast, AC transport & coordinator</span>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  aria-label={isSubmitting ? "Submitting booking..." : "Confirm booking"}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Confirm Booking
                    </>
                  )}
                </button>

                {/* Terms */}
                <p className="text-xs sm:text-sm text-gray-600 text-center">
                  By booking, you agree to our{" "}
                  <a href="/terms" className="text-orange-600 hover:underline">
                    terms and conditions
                  </a>
                  {" "}and{" "}
                  <a href="/privacy" className="text-orange-600 hover:underline">
                    privacy policy
                  </a>
                </p>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add required CSS animations */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </section>
  );
}

export default BangaloreSection;